import {
  useAddFullProductKitToCartMutation,
  useAddProductKitToCartMutation,
  useAddProductVariantToCartMutation,
} from '@graphql/mutations/__generated__/product-reflection.generated'
import { useMeQuery } from '@graphql/queries/__generated__/me.generated'
import { useVariantKitDataLazyQuery } from '@graphql/queries/__generated__/product-variant-kit.generated'
import _ from 'lodash'
import { useSnackbar } from 'notistack'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Redirect, useParams } from 'react-router-dom'
import { useQueryParams } from 'use-query-params'
import { GlobalDialogFormsParams } from '@components/GlobalDialogForms'
import { MetaWrapper } from '../../components/Meta'
import VariantKitPanel from '../../components/Product/VariantKitPanel/VariantKitPanel'
import useGlobalLoader from '../../hooks/useGlobalLoader'
import { formErrorsToString, mutationWarningsToMessages } from '../../misc/utils'
import { DataLayerItem, googlePushProductAddToCart, googlePushViewItem } from '../../utils/dataLayer'
import Page404 from '../Page404'
import ProductContext, { IProductContext } from './context/ProductContext'
import Page from './Page'
import { extractReflectionMeta, getAttributesFromProduct } from './utils'
import { useReflectionFullInfoQuery } from '@graphql/queries/__generated__/reflection.generated'
import { maybe } from '../../utils/misc'
import {
  ProductVariantFullInfoQueryResult,
  useProductVariantFullInfoLazyQuery,
} from '@graphql/queries/__generated__/product-variant.generated'
import { downloadFile } from '../../utils/order'
import { RecentlyViewedContext } from '@components/RecentlyViewed/RecentlyViewedProvider'
import { SITE_NAME } from '../../../constants'
import { CartTypeEnum } from '../../../generated/gqlTypes'

const View: React.FC<{
  isModal?: boolean
  initialId?: number | string
  setIsOpenProductDialog?({}: { pk: number; slug: string }): void
}> = ({ isModal, initialId, setIsOpenProductDialog }) => {
  const { addId: addStorageViewedId } = useContext(RecentlyViewedContext)
  const { enqueueSnackbar } = useSnackbar()
  const [, setParams] = useQueryParams(GlobalDialogFormsParams)
  const [selectedVariant, setSelectedVariant] = useState<
    ProductVariantFullInfoQueryResult['data']['productVariant'] | null
  >(null)
  const [attributes, setAttributes] = useState<
    {
      name: string
      values: string[]
    }[]
  >([])
  const [selectedAttrs, setSelectedAttrs] = useState(null)
  const [quantity, setQuantity] = useState(1)
  const [kitLoading, setKitLoading] = useState(false)
  const [addedVariant, setAddedVariant] = useState<IProductContext['lastAddedVariantInfo']>(null)
  const [kitFormData, setKitFormData] = useState(null)
  const [isOpenKitPanel, setIsOpenKitPanel] = useState(false)
  const { setLoading } = useGlobalLoader()
  const [localLoading, setLocalLoading] = useState(false)
  const { id } = useParams<{ id: string }>()

  const dataUser = useMeQuery()
  const reflectionQueryResult = useReflectionFullInfoQuery({
    variables: { id: String(initialId || id) },
    context: { notBatching: true },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      const props = maybe(() => data.reflectionDetails.reflection.product.variantsProperties, [])
      const attrs = getAttributesFromProduct(props)
      setAttributes(attrs)
      const selectedAttrs = {}
      if (attrs.length > 0) {
        attrs.forEach(attr => {
          if (attr.values.length === 1) {
            selectedAttrs[attr.name] = attr.values[0]
          }
        })
      } else if (reflectionQueryResult.data?.reflectionDetails?.reflection && attrs.length === 0) {
        matchVariantFromSelection({})
      }
      setSelectedAttrs(_.isEmpty(selectedAttrs) ? null : selectedAttrs)
      setQuantity(1)
      addStorageViewedId(data.reflectionDetails.reflection.pk)
      const reflection = data.reflectionDetails.reflection
      if (reflection?.product) {
        const price = reflection.product.priceRange.discount.min
        const items: DataLayerItem[] = [
          {
            item_id: reflection.product.sku,
            item_name: reflection.name,
            index: 0,
            quantity: 1,
            price: price,
          },
        ]
        googlePushViewItem({ currency: reflection.product.priceRange.currency, value: price, items })
      }
    },
    onError: error => {
      enqueueSnackbar(error.message, { variant: 'error' })
      setSelectedVariant(null)
    },
  })
  const [loadProductVariant, productVariantQueryResult] = useProductVariantFullInfoLazyQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      setSelectedVariant(data?.productVariant)
      setQuantity(1)
      setLocalLoading(false)
    },
    onError: error => {
      enqueueSnackbar(error.message, { variant: 'error' })
      setSelectedVariant(null)
      setQuantity(1)
      setLocalLoading(false)
    },
  })

  const [loadKitData, dataKitQuery] = useVariantKitDataLazyQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      const res = data.productVariant.customizeKitData
      setKitFormData(res)
      setIsOpenKitPanel(true)
    },
    onError: error => {
      enqueueSnackbar(error.message, {
        variant: 'error',
      })
    },
  })
  const [addVariantToBag] = useAddProductVariantToCartMutation({
    refetchQueries: ['Cart', 'CartLines', 'CartShippingDiscounts'],
    awaitRefetchQueries: true,
  })
  const [addFullKitToBag] = useAddFullProductKitToCartMutation({
    refetchQueries: ['Cart', 'CartLines', 'CartShippingDiscounts'],
    awaitRefetchQueries: true,
    onCompleted: data =>
      mutationWarningsToMessages(data.addFullKitToCart.warnings).forEach(message =>
        enqueueSnackbar(message, { variant: 'warning' })
      ),
  })
  const [addKitToBag] = useAddProductKitToCartMutation({
    refetchQueries: ['Cart', 'CartLines', 'CartShippingDiscounts'],
    awaitRefetchQueries: true,
    onCompleted: data =>
      mutationWarningsToMessages(data.addKitToCart.warnings).forEach(message =>
        enqueueSnackbar(message, { variant: 'warning' })
      ),
  })
  useEffect(() => {
    // setAddedVariant(null)
    // setSelectedVariant(null)
    // setSelectedAttrs({})
    // setQuantity(1)
    return () => {
      setLoading(false)
      setAddedVariant(null)
      setSelectedVariant(null)
      setSelectedAttrs({})
      setQuantity(1)
    }
  }, [reflectionQueryResult.data])

  useEffect(() => {
    if (selectedAttrs && !_.isEmpty(selectedAttrs)) {
      matchVariantFromSelection(selectedAttrs || {})
    }
  }, [selectedAttrs])

  useEffect(() => {
    setLoading(reflectionQueryResult.loading && !reflectionQueryResult.data)
  }, [reflectionQueryResult])

  useEffect(() => {
    if (addedVariant) {
      const categoryName = maybe<string>(
        () => reflectionQueryResult.data?.reflectionDetails.reflection.categories[0]?.name,
        ''
      )
      const supplyLines: {
        quantity: number
        price: number
      }[] = addedVariant.supplyLinesJson
      const reflection = reflectionQueryResult.data?.reflectionDetails.reflection
      if (reflection) {
        const price = !addedVariant.variant.isSupply
          ? addedVariant.variant.priceRange.discount.min
          : _.sumBy(supplyLines, line => line.quantity * line.price)
        const items: DataLayerItem[] = [
          {
            item_id: addedVariant.variant.sku,
            item_name: reflection.name,
            index: 0,
            item_brand: reflection.product.manufacturer?.name || SITE_NAME,
            item_category: categoryName,
            item_variant: addedVariant.variant.properties
              .map(prop => `${prop.attributeName}: ${prop.valueName}`)
              .join(),
            price,
            quantity,
          },
        ]
        googlePushProductAddToCart({ currency: reflection.product.priceRange.currency, value: price * quantity, items })
      }
    }
  }, [addedVariant])
  const matchVariantFromSelection = async (attrs: { [key: string]: string }) => {
    if (
      Object.values(attrs).find(value => value?.toLowerCase().includes('click to select')) ||
      Object.values(attrs).length < Object.keys(attributes).length
    ) {
      setSelectedVariant(null)
      return
    }
    // setLocalLoading(true)
    void loadProductVariant({
      variables: {
        productPk: reflectionQueryResult.data.reflectionDetails.reflection.product.pk,
        cartType: CartTypeEnum.Default,
        attrs: Object.entries(attrs).map(([key, val]) => ({
          attrName: key,
          valueName: val,
        })),
      },
    })
  }

  const onChangeQuantity = (val: string | number) => {
    if (+val > 0) {
      setQuantity(+val)
    }
  }

  const onAddToBag = async e => {
    e.preventDefault()
    setLocalLoading(true)
    try {
      const response = await addVariantToBag({
        variables: {
          pk: selectedVariant.pk,
          quantity: quantity,
        },
      })
      const errors = response.data.addVariantToCart.errors
      if (!errors || _.isEmpty(errors)) {
        setAddedVariant(response.data?.addVariantToCart)
        setLocalLoading(false)
        setSelectedVariant(null)
        setSelectedAttrs({})
        await Promise.all([reflectionQueryResult.refetch()])
      } else {
        enqueueSnackbar(formErrorsToString(errors), {
          variant: 'error',
        })
      }
    } catch (e) {
      enqueueSnackbar(e.message, {
        variant: 'error',
      })
    }
    setLocalLoading(false)
  }
  const onAddToBagFullKit = async e => {
    e.preventDefault()
    setLocalLoading(true)
    try {
      const res = await addFullKitToBag({
        variables: { pk: selectedVariant.pk, quantity },
      })
      setAddedVariant(res.data?.addFullKitToCart)
      setSelectedVariant(null)
      setSelectedAttrs({})
      setLocalLoading(false)
      await Promise.all([reflectionQueryResult.refetch()])
    } catch (e) {
      enqueueSnackbar(e.message, {
        variant: 'error',
      })
    }
    setLocalLoading(false)
  }
  const onCustomizeKit = e => {
    e.preventDefault()
    setLocalLoading(true)
    loadKitData({
      variables: { pk: selectedVariant.pk },
    })
  }

  const onSubmitKit = async (supplyLine = null, variants: { pk: number; quantity: number }[], setAsDefault = false) => {
    setKitLoading(true)
    try {
      const response = await addKitToBag({
        variables: {
          pk: selectedVariant.pk,
          variants: variants.map(el => ({
            pk: el.pk,
            quantity: el.quantity,
          })),
          setAsDefault,
          quantity,
        },
      })
      const errors = response.data?.addKitToCart.errors || []
      if (errors.length) {
        throw Error(errors[0].messages[0])
      }
      setAddedVariant(response.data?.addKitToCart)
      onCloseKitPanel()
      setLocalLoading(false)
      await Promise.all([reflectionQueryResult.refetch()])
    } catch (e) {
      enqueueSnackbar(e.message, {
        variant: 'error',
      })
    }
    setKitLoading(false)
    onCloseKitPanel()
  }

  const onOpenKitPanel = () => {
    setIsOpenKitPanel(true)
  }
  const onCloseKitPanel = () => {
    setIsOpenKitPanel(false)
    setLocalLoading(false)
  }
  const onGooglePushProductDetails = useCallback(() => {
    const categoryName = maybe<string>(
      () => reflectionQueryResult.data?.reflectionDetails.reflection.categories[0]?.name,
      ''
    )
    console.log(categoryName)
  }, [reflectionQueryResult.data])

  const reflectionAllImages = useMemo(() => {
    return maybe(() => reflectionQueryResult.data.reflectionDetails.reflection.product.images, [])
  }, [reflectionQueryResult.data, selectedVariant])

  const onDownloadFiles = async (url: string) => {
    if (!url) {
      setParams({ global_dialog_form: 'contactUs' }, 'replaceIn')
    } else {
      await downloadFile(
        url,
        () => {
          setLoading(true)
        },
        async () => {
          await reflectionQueryResult.refetch()
          setLoading(false)
        },
        error => {
          setLoading(false)
        },
        (msg, variant) => enqueueSnackbar(msg, { variant })
      )
    }
  }

  const redirectUrl = reflectionQueryResult.data?.reflectionDetails.redirectUrl
    ? new URL(reflectionQueryResult.data?.reflectionDetails.redirectUrl)
    : null

  if (isModal) {
    return (
      <>
        {!!reflectionQueryResult.data?.reflectionDetails?.reflection && (
          <ProductContext.Provider
            value={{
              selectedVariant: selectedVariant,
              reflection: reflectionQueryResult.data?.reflectionDetails?.reflection,
              formValues: {
                quantity,
                selectedAttrs,
              },
              formMethods: {
                onChangeQuantity,
                setSelectedAttrs,
              },
              loading:
                localLoading ||
                productVariantQueryResult.loading ||
                reflectionQueryResult.loading ||
                dataKitQuery.loading,
              me: dataUser.data?.me,
              attributes,
              images: (selectedVariant?.allImages || reflectionAllImages).map(node => ({
                id: node.id,
                alt: node.alt,
                thumbnail: node.thumbnailUrl,
                url: node.url,
                width: node.width,
                height: node.height,
              })),
              lastAddedVariantInfo: addedVariant,
              isModal,

              matchVariantFromSelection,
              onAddToBag,
              onAddToBagFullKit,
              onCustomizeKit,

              setLastAddedVariantInfo: setAddedVariant,
              onDownloadFiles,
              onChangeSelectedVariant: async attrs => {},
              setIsOpenProductDialog,
            }}
          >
            <Page onGooglePushProductDetails={onGooglePushProductDetails} isModal={isModal} initialId={initialId} />
          </ProductContext.Provider>
        )}
        <VariantKitPanel
          isOpen={isOpenKitPanel}
          onClosePanel={onCloseKitPanel}
          onOpen={onOpenKitPanel}
          onSubmit={onSubmitKit}
          loading={kitLoading}
          data={kitFormData}
        />
      </>
    )
  }
  return (
    <>
      {redirectUrl && <Redirect to={redirectUrl.pathname} />}
      {!redirectUrl && !reflectionQueryResult.loading && !reflectionQueryResult.data?.reflectionDetails.reflection && (
        <Page404 />
      )}
      {!!reflectionQueryResult.data?.reflectionDetails?.reflection && (
        <MetaWrapper
          meta={extractReflectionMeta(
            reflectionQueryResult.data?.reflectionDetails,
            selectedVariant?.allImages || reflectionAllImages
          )}
          titleDetails={
            reflectionQueryResult.data?.reflectionDetails?.reflection?.product?.name ||
            reflectionQueryResult.data?.reflectionDetails?.reflection?.name ||
            ''
          }
        >
          <ProductContext.Provider
            value={{
              selectedVariant: selectedVariant,
              reflection: reflectionQueryResult.data?.reflectionDetails?.reflection,
              formValues: {
                quantity,
                selectedAttrs,
              },
              formMethods: {
                onChangeQuantity,
                setSelectedAttrs,
              },
              loading: localLoading || reflectionQueryResult.loading || dataKitQuery.loading,
              me: dataUser.data?.me,
              attributes,
              images: (selectedVariant?.allImages || reflectionAllImages).map(node => ({
                id: node.id,
                alt: node.alt,
                thumbnail: node.thumbnailUrl,
                url: node.url,
                width: node.width,
                height: node.height,
              })),
              lastAddedVariantInfo: addedVariant,

              matchVariantFromSelection,
              onAddToBag,
              onAddToBagFullKit,
              onCustomizeKit,

              setLastAddedVariantInfo: setAddedVariant,
              onDownloadFiles,
              onChangeSelectedVariant: async attrs => {},
            }}
          >
            <Page onGooglePushProductDetails={onGooglePushProductDetails} isModal={isModal} initialId={initialId} />
          </ProductContext.Provider>
        </MetaWrapper>
      )}
      <VariantKitPanel
        isOpen={isOpenKitPanel}
        onClosePanel={onCloseKitPanel}
        onOpen={onOpenKitPanel}
        onSubmit={onSubmitKit}
        loading={kitLoading}
        data={kitFormData}
      />
    </>
  )
}

export default React.memo(View)
